import { X, Activity, Cable, Boxes, Split, RadioTower, Flag, Info, Copy, FileDown } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const ICONS = { cable: Cable, splice: Boxes, splitter: Split, olt: RadioTower, end: Flag, info: Info };
const TYPE_LABEL = { cable: "Kabel", splice: "Mufa", splitter: "Splitter", olt: "OLT", end: "Koniec", info: "Info" };

export default function FiberTracePanel({ trace, onClose, onStepClick }) {
  const { t } = useI18n();
  if (!trace) return null;
  const st = trace.budget_status;
  const meta = {
    ok: { label: t("W normie"), cls: "bg-green-900/40 text-green-400 border-green-700" },
    warn: { label: t("Ostrzeżenie"), cls: "bg-yellow-900/40 text-yellow-400 border-yellow-700" },
    fail: { label: t("Przekroczony budżet"), cls: "bg-red-900/40 text-red-400 border-red-700" },
  }[st] || { label: st, cls: "bg-slate-800 text-slate-400 border-slate-600" };
  const attColor = trace.remaining_db < 0 ? "text-red-400" : trace.remaining_db < 3 ? "text-yellow-400" : "text-green-400";

  const copyRoute = async () => {
    const lines = [`Trasa włókna: Kabel ${trace.cableNumber} · wł. ${trace.fiber}`];
    (trace.steps || []).forEach((s) => {
      lines.push(`  ${t(TYPE_LABEL[s.type] || s.type)}: ${s.name}${s.detail ? " (" + s.detail + ")" : ""} · Σ ${s.atten_total} dB`);
    });
    lines.push(`Tłumienie łącza: ${trace.total_atten_db} dB / budżet ${trace.budget_db} dB · zapas ${trace.remaining_db} dB (${meta.label})`);
    try { await navigator.clipboard.writeText(lines.join("\n")); toast.success(t("Skopiowano trasę")); }
    catch { toast.error(t("Nie udało się skopiować")); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/cables/${trace.cable_id}/fiber-trace-pdf`, { params: { fiber: trace.fiber }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `trasa_wlokna_${trace.cableNumber}_wl${trace.fiber}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error(t("Nie udało się wygenerować PDF")); }
  };

  return (
    <div className="absolute inset-0 bg-slate-900 flex flex-col" data-testid="fiber-trace-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700 bg-gradient-to-r from-cyan-900/40 to-slate-900">
        <div className="flex items-center gap-2">
          <Activity size={18} className="text-cyan-400" />
          <h2 className="font-heading font-bold text-base">{t("Trasa włókna")}</h2>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={downloadPdf} data-testid="fiber-trace-pdf" title={t("PDF trasy włókna")}
            className="text-slate-300 hover:text-cyan-400 p-1"><FileDown size={16} /></button>
          <button onClick={copyRoute} data-testid="fiber-trace-copy" title={t("Kopiuj trasę")}
            className="text-slate-300 hover:text-cyan-400 p-1"><Copy size={16} /></button>
          <button onClick={onClose} data-testid="fiber-trace-close" className="text-slate-400 hover:text-white"><X size={18} /></button>
        </div>
      </div>

      <div className="px-4 py-3 border-b border-slate-800">
        <div className="text-sm font-medium">{t("Kabel")} {trace.cableNumber} · {t("włókno")} #{trace.fiber}</div>
        <div className="text-xs text-slate-400">{(trace.steps || []).length} {t("kroków trasy sygnału w stronę OLT")}</div>
      </div>

      <div className="grid grid-cols-3 gap-px bg-slate-800 border-b border-slate-700">
        <Stat label={t("Tłumienie")} value={`${trace.total_atten_db} dB`} valueClass="text-orange-300" />
        <Stat label={t("Budżet")} value={`${trace.budget_db} dB`} />
        <Stat label={t("Zapas")} value={`${trace.remaining_db} dB`} valueClass={attColor} />
      </div>

      <div className="px-4 py-2 border-b border-slate-800 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{t("Status budżetu")}</span>
        <span className={`text-[11px] px-2 py-0.5 rounded-full border ${meta.cls}`} data-testid="fiber-trace-status">{meta.label}</span>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <div className="relative pl-6">
          <div className="absolute left-[9px] top-2 bottom-2 w-0.5 bg-gradient-to-b from-cyan-400 to-blue-600" />
          {(trace.steps || []).map((s, i) => {
            const Icon = ICONS[s.type] || Boxes;
            const clickable = s.lat != null && s.lng != null;
            return (
              <div key={i} className="relative mb-4" data-testid={`fiber-trace-step-${i}`}>
                <div className="absolute -left-6 top-0.5 w-[19px] h-[19px] rounded-full bg-slate-800 border-2 border-cyan-400 flex items-center justify-center">
                  <Icon size={10} className="text-cyan-400" />
                </div>
                <div onClick={() => clickable && onStepClick && onStepClick(s)}
                  data-testid={`fiber-trace-step-goto-${i}`}
                  className={`bg-slate-800 border border-slate-700 rounded-md px-3 py-2 ${clickable ? "cursor-pointer hover:border-cyan-500 hover:bg-slate-700/60" : ""}`}
                  title={clickable ? t("Pokaż na mapie") : ""}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-[10px] uppercase tracking-wide text-cyan-400 font-semibold shrink-0">{t(TYPE_LABEL[s.type] || s.type)}</span>
                      <span className="text-sm font-medium truncate">{s.name}</span>
                    </div>
                    <span className="text-xs font-mono text-slate-300 shrink-0">Σ {s.atten_total} dB</span>
                  </div>
                  {s.detail && <div className="text-xs text-slate-400 mt-0.5 font-mono">{s.detail}{s.atten_step ? ` · +${s.atten_step} dB` : ""}</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, valueClass = "text-white" }) {
  return (
    <div className="bg-slate-900 px-3 py-2.5 text-center">
      <div className={`font-mono text-sm font-semibold ${valueClass}`}>{value}</div>
      <div className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</div>
    </div>
  );
}
